import { useEffect, useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import PasswordInput from '../components/PasswordInput'
import toast from 'react-hot-toast'
import useDocumentTitle from '../hooks/useDocumentTitle'

const RESEND_COOLDOWN_SECONDS = 60

export default function LoginPage() {
  useDocumentTitle('Login')

  const {
    user,
    loading,
    signIn,
    sendPasswordResetEmail,
    verifyPasswordResetCode,
  } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [mode, setMode] = useState('login')
  const [submitting, setSubmitting] = useState(false)
  const [resetEmailSentTo, setResetEmailSentTo] = useState('')
  const [resetCode, setResetCode] = useState('')
  const [resendAvailableAt, setResendAvailableAt] = useState(0)
  const [resendSecondsLeft, setResendSecondsLeft] = useState(0)
  const awaitingResetCode =
    mode === 'forgot-password' && Boolean(resetEmailSentTo)

  useEffect(() => {
    if (location.state?.message) {
      toast.success(location.state.message, {
        id: 'login-status-message',
      })
      navigate(location.pathname, { replace: true, state: null })
    }
  }, [location.pathname, location.state, navigate])

  useEffect(() => {
    if (!resendAvailableAt) return undefined

    const tick = () => {
      setResendSecondsLeft(
        Math.max(0, Math.ceil((resendAvailableAt - Date.now()) / 1000))
      )
    }

    tick()
    const intervalId = window.setInterval(tick, 1000)

    return () => window.clearInterval(intervalId)
  }, [resendAvailableAt])

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
      </div>
    )
  }

  if (user) {
    // Verifying the reset code signs the user in; send them on to set a new password.
    return <Navigate to={awaitingResetCode ? '/reset-password' : '/'} replace />
  }

  const sendResetCode = async (targetEmail) => {
    setSubmitting(true)

    try {
      await sendPasswordResetEmail(targetEmail)
      setResetEmailSentTo(targetEmail)
      setResetCode('')
      setResendSecondsLeft(RESEND_COOLDOWN_SECONDS)
      setResendAvailableAt(Date.now() + RESEND_COOLDOWN_SECONDS * 1000)
      toast.success('Password reset email sent')
    } catch (err) {
      toast.error(err.message || 'Unable to send reset email')
    } finally {
      setSubmitting(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (awaitingResetCode) {
      if (!resetCode.trim()) {
        toast.error('Please enter the verification code')
        return
      }

      setSubmitting(true)

      try {
        await verifyPasswordResetCode(resetEmailSentTo, resetCode.trim())
      } catch (err) {
        toast.error(err.message || 'Invalid or expired verification code')
      } finally {
        setSubmitting(false)
      }

      return
    }

    if (mode === 'forgot-password') {
      if (!email.trim()) {
        toast.error('Please enter your email address')
        return
      }

      await sendResetCode(email.trim())
      return
    }

    if (!email.trim() || !password.trim()) {
      toast.error('Please fill in all fields')
      return
    }

    setSubmitting(true)

    try {
      await signIn(email.trim(), password)
      toast.success('Welcome back!')
    } catch (err) {
      toast.error(err.message || 'Invalid credentials')
    } finally {
      setSubmitting(false)
    }
  }

  const switchToForgotPassword = () => {
    setMode('forgot-password')
    setPassword('')
  }

  const switchToLogin = () => {
    setMode('login')
    setResetEmailSentTo('')
    setResetCode('')
    setResendAvailableAt(0)
    setResendSecondsLeft(0)
  }

  return (
    <div className="login-page">
      <div className="login-left">
        <div className="login-branding">
          <div className="login-logo">
            <div className="logo-icon large">K</div>
          </div>
          <h1>Kleezy Admin</h1>
          <p>Back-office management platform for Kleezy real estate services</p>
        </div>

        <div className="login-decoration">
          <div className="decoration-circle c1" />
          <div className="decoration-circle c2" />
          <div className="decoration-circle c3" />
        </div>
      </div>

      <div className="login-right">
        <div className="login-form-container">
          <div className="login-form-header">
            <h2>{mode === 'login' ? 'Sign in' : 'Forgot password'}</h2>
            <p>
              {mode === 'login'
                ? 'Enter your credentials to access the admin panel'
                : 'Enter your email and we will send you a verification code'}
            </p>
          </div>

          <form className="login-form" onSubmit={handleSubmit}>
            {awaitingResetCode ? (
              <div className="auth-message auth-message-success">
                Reset email has been sent to <strong>{resetEmailSentTo}</strong>.
                Enter the verification code from that email to set a new
                password.
              </div>
            ) : null}

            <div className="form-group">
              <label htmlFor="email">Email address</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@kleezy.com"
                disabled={submitting || awaitingResetCode}
                autoComplete="email"
                autoFocus
              />
            </div>

            {awaitingResetCode ? (
              <div className="form-group">
                <label htmlFor="reset-code">Verification code</label>
                <input
                  id="reset-code"
                  type="text"
                  inputMode="numeric"
                  value={resetCode}
                  onChange={(e) =>
                    setResetCode(e.target.value.replace(/\D/g, ''))
                  }
                  placeholder="Code from the email"
                  disabled={submitting}
                  autoComplete="one-time-code"
                  maxLength={10}
                  autoFocus
                />
              </div>
            ) : null}

            {mode === 'login' ? (
              <div className="form-group">
                <label htmlFor="password">Password</label>
                <PasswordInput
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="********"
                  disabled={submitting}
                  autoComplete="current-password"
                />
              </div>
            ) : null}

            <button
              type="submit"
              className="btn btn-primary btn-full"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <span className="loading-spinner small" />
                  {mode === 'login'
                    ? 'Signing in...'
                    : awaitingResetCode
                      ? 'Verifying code...'
                      : 'Sending reset email...'}
                </>
              ) : mode === 'login' ? (
                'Sign in'
              ) : awaitingResetCode ? (
                'Verify code'
              ) : (
                'Send reset email'
              )}
            </button>
          </form>

          <div className="auth-link-group">
            {awaitingResetCode ? (
              <button
                type="button"
                className="btn btn-ghost auth-link-button"
                onClick={() => sendResetCode(resetEmailSentTo)}
                disabled={submitting || resendSecondsLeft > 0}
              >
                {resendSecondsLeft > 0
                  ? `Resend code in ${resendSecondsLeft}s`
                  : 'Resend code'}
              </button>
            ) : null}
            {mode === 'login' ? (
              <button
                type="button"
                className="btn btn-ghost auth-link-button"
                onClick={switchToForgotPassword}
                disabled={submitting}
              >
                Forgot your password?
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-ghost auth-link-button"
                onClick={switchToLogin}
                disabled={submitting}
              >
                Back to sign in
              </button>
            )}
          </div>

          <div className="login-footer">
            <p>
              Kleezy Admin Panel &middot; {new Date().getFullYear()}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
